import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CategoryNav from './CategoryNav';
import { useSiteData } from '../data/SiteDataContext';
import { getSeriesPath } from '../lib/format';
import {
    classifyGroups,
    collectGroupTags,
    countTagUsage,
    tagLink,
} from '../lib/tagIndex';

const MAILCHIMP_ACTION = 'https://thediscerner.us15.list-manage.com/subscribe/post?u=65a58ae0061e7aa238359aeaa&id=8871a42b41&f_id=00ebc2e1f0';

// The original sidebar's INDEX card tabs, restyled for the new theme.
const SIDEBAR_TABS = [
    { id: 'subjects', label: 'Subjects' },
    { id: 'geography', label: 'Geography' },
    { id: 'persons', label: 'Persons' },
];

function SiteSidebar() {
    const { series, posts, groups } = useSiteData();
    const [activeTab, setActiveTab] = useState('subjects');

    const { geography, persons } = useMemo(() => classifyGroups(groups), [groups]);

    // Geography/persons tags with per-post usage counts; unused tags are
    // dropped, matching how empty categories are hidden.
    const { geographyTags, personTags } = useMemo(() => {
        const usage = countTagUsage(posts);
        const withCounts = (tagGroups) => collectGroupTags(tagGroups)
            .map((tag) => ({ ...tag, count: usage.get(tag.value) || 0 }))
            .filter((tag) => tag.count > 0);
        return {
            geographyTags: withCounts(geography),
            personTags: withCounts(persons),
        };
    }, [posts, geography, persons]);

    const visibleTabs = SIDEBAR_TABS.filter((tab) => {
        if (tab.id === 'geography') return geographyTags.length > 0;
        if (tab.id === 'persons') return personTags.length > 0;
        return true;
    });

    const tagPanel = activeTab === 'geography'
        ? geographyTags
        : (activeTab === 'persons' ? personTags : null);

    return (
        <aside className="sidebar" role="complementary" aria-label="Sidebar">
            <div className="sidebar-section">
                <div className="sidebar-section-title">About</div>
                <div className="sidebar-about">
                    <p className="sidebar-about-text">
                        Orthodox Christian laymen writing on agathology and ponerology
                        — the study of good and evil as they manifest in culture,
                        power, media, and sacred history. Anonymous by design.
                    </p>
                    <Link className="sidebar-about-link" to="/about">Read orientation file →</Link>
                </div>
            </div>

            <div className="sidebar-section">
                <div className="sidebar-section-title">Categories</div>
                {visibleTabs.length > 1 && (
                    <div className="sidebar-tabs" role="tablist" aria-label="Category index sections">
                        {visibleTabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={tab.id === activeTab}
                                className={`sidebar-tab-btn${tab.id === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
                {tagPanel ? (
                    <nav className="category-nav" aria-label={`${activeTab} tags`}>
                        {tagPanel.map((tag) => (
                            <Link key={tag.value} to={tagLink(tag.value)}>
                                {tag.label}
                                <span className="cat-count">{tag.count}</span>
                            </Link>
                        ))}
                    </nav>
                ) : (
                    <CategoryNav />
                )}
                <Link className="sidebar-view-tags-btn" to="/tags">View all tags →</Link>
            </div>

            {series.length > 0 && (
                <div className="sidebar-section">
                    <div className="sidebar-section-title">Series</div>
                    {series.map((entry) => (
                        <div key={entry.key} style={{ marginBottom: '.85rem' }}>
                            <Link
                                className="series-title-link"
                                to={getSeriesPath(entry)}
                            >
                                {entry.name}
                            </Link>
                        </div>
                    ))}
                    <Link className="sidebar-view-tags-btn" to="/series">All series →</Link>
                </div>
            )}

            <div className="sidebar-section">
                <div className="sidebar-section-title">Stay Informed</div>
                <div className="email-signup">
                    <p>New writing delivered to your inbox</p>
                    <form
                        action={MAILCHIMP_ACTION}
                        method="post"
                        id="mc-embedded-subscribe-form"
                        name="mc-embedded-subscribe-form"
                        noValidate
                    >
                        <input
                            type="email"
                            name="EMAIL"
                            id="mce-EMAIL"
                            placeholder="your@email.com"
                            required
                        />
                        <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                            <input type="text" name="b__" tabIndex={-1} defaultValue="" />
                        </div>
                        <button type="submit">Subscribe</button>
                    </form>
                </div>
            </div>
        </aside>
    );
}

export default SiteSidebar;
